import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, ChevronRight, Car, Users } from 'lucide-react';
import api from '@/lib/axios';
import { resolveImageUrl, listingUrl } from '@/lib/listing';
import { useSeo, useJsonLd } from '../hooks/useSeo';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';
import { AdInlineList } from '@/components/marketplace/ads/AdSlot';
import { DISTRICTS, DISTRICT_MAP } from '@/lib/districts';
import AlertSubscribeForm from '@/components/marketplace/AlertSubscribeForm';

const SITE = 'https://marketplacetvde.pt';

function capitalize(s) {
  return (s || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function MarketplaceCityPage({ tipo }) {
  const { slugId } = useParams();
  const isCategory = !slugId;
  const cidadeSlug = slugId || '';
  const label = isCategory ? 'Portugal' : (DISTRICT_MAP[cidadeSlug] || capitalize(cidadeSlug));
  const cidadeQuery = isCategory ? '' : cidadeSlug.replace(/-/g, ' ');
  const isViatura = tipo === 'viatura';
  const tipoPlural = isViatura ? 'viaturas' : 'motoristas';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const title = isViatura
    ? `Viaturas TVDE em ${label} | Marketplace TVDE`
    : `Motoristas TVDE em ${label} | Marketplace TVDE`;
  const description = isViatura
    ? `Viaturas TVDE para aluguer em ${label}. Veja anúncios de carros para Uber e Bolt em ${label} no Marketplace TVDE de Portugal.`
    : `Motoristas TVDE disponíveis em ${label}. Encontre condutores para a sua viatura Uber e Bolt em ${label}.`;
  const path = isCategory ? `/${tipoPlural}` : `/${tipoPlural}/${cidadeSlug}`;

  useSeo({ title, description, path });

  const breadcrumbItems = [
    { '@type': 'ListItem', position: 1, name: 'Início', item: `${SITE}/` },
    { '@type': 'ListItem', position: 2, name: isViatura ? 'Viaturas' : 'Motoristas', item: `${SITE}/${tipoPlural}` },
  ];
  if (!isCategory) {
    breadcrumbItems.push({ '@type': 'ListItem', position: 3, name: label, item: `${SITE}${path}` });
  }
  useJsonLd('breadcrumb-city', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  });

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = { tipo, limit: 40 };
    if (cidadeQuery) params.cidade = cidadeQuery;
    api
      .get('/marketplace/publicacoes', { params })
      .then((res) => { if (active) { setItems(res.data.items || []); setError(false); } })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [tipo, cidadeQuery, reloadKey]);

  const H1 = isViatura ? `Viaturas TVDE em ${label}` : `Motoristas TVDE em ${label}`;

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900" data-testid="marketplace-city-page">
      {/* Nav simples */}
      <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between gap-3 px-4 py-3 md:px-8">
          <Link to="/" className="flex items-center gap-2.5" data-testid="city-logo-home">
            <img src="/icon-192x192.png" alt="Marketplace TVDE" className="h-8 w-8 rounded-lg object-cover" />
            <span className="text-base font-bold">Marketplace TVDE</span>
          </Link>
          <Link
            to="/"
            className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            data-testid="city-back-home"
          >
            Ver todos os anúncios
          </Link>
        </div>
      </nav>

      <main className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8">
        {/* Breadcrumb visível */}
        <nav aria-label="Breadcrumb" className="mb-5 flex flex-wrap items-center gap-1 text-sm text-slate-500" data-testid="breadcrumb">
          <Link to="/" className="hover:text-emerald-600">Início</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to={`/${tipoPlural}`} className="hover:text-emerald-600">{isViatura ? 'Viaturas' : 'Motoristas'}</Link>
          {!isCategory && (
            <>
              <ChevronRight className="h-4 w-4" />
              <span className="font-semibold text-slate-700">{label}</span>
            </>
          )}
        </nav>

        <header className="mb-8">
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl" data-testid="city-h1">
            {H1}
          </h1>
          <p className="mt-3 max-w-3xl text-base text-slate-600">
            {isViatura
              ? `Encontre viaturas TVDE disponíveis para aluguer em ${label} e ligue-se a quem já trabalha com a Uber e a Bolt. Todos os anúncios são revistos antes de publicados.`
              : `Motoristas TVDE ativos em ${label} à procura de viatura para trabalhar com a Uber e a Bolt. Contacte diretamente através do Marketplace TVDE.`}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to="/anunciar-viatura"
              className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-600"
              data-testid="city-cta-publish"
            >
              <Car className="h-4 w-4" /> Anunciar viatura
            </Link>
            <Link
              to="/publicar-procura"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
              data-testid="city-cta-driver"
            >
              <Users className="h-4 w-4" /> Anunciar Procura de Viatura
            </Link>
          </div>
        </header>

        {/* Listagens */}
        {loading ? (
          <p className="text-slate-500" data-testid="city-loading">A carregar anúncios…</p>
        ) : error ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center" data-testid="city-error">
            <p className="text-base font-bold text-amber-900">Não foi possível carregar os anúncios. Pode ser um problema temporário de ligação.</p>
            <button
              onClick={() => setReloadKey((k) => k + 1)}
              className="mt-4 inline-flex rounded-full bg-amber-500 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-amber-600"
              data-testid="city-retry"
            >
              Tentar novamente
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center" data-testid="city-empty">
            <p className="text-lg font-semibold text-slate-800">
              Ainda não há {isViatura ? 'viaturas' : 'motoristas'} {isCategory ? '' : `em ${label}`} de momento.
            </p>
            <p className="mt-2 text-sm text-slate-500">
              {isViatura
                ? 'Seja o primeiro a anunciar a sua viatura nesta zona.'
                : 'Publique o seu perfil e seja encontrado por quem tem viaturas disponíveis.'}
            </p>
            <Link
              to={isViatura ? '/anunciar-viatura' : '/publicar-procura'}
              className="mt-4 inline-flex rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-emerald-600"
              data-testid="city-empty-cta"
            >
              {isViatura ? 'Anunciar viatura' : 'Publicar procura'}
            </Link>
            {isViatura && (
              <div className="mx-auto mt-6 max-w-md">
                <AlertSubscribeForm distrito={isCategory ? '' : label} finalidade="aluguer" />
              </div>
            )}
          </div>
        ) : (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="city-listings-grid">
            <AdInlineList posicao={isViatura ? 'rental_list_inline' : 'drivers_list_inline'} distrito={isCategory ? '' : label}>
            {items.map((item) => {
              const foto = (item.detalhes?.fotos || [])[0];
              const preco = item.detalhes?.valor_semanal;
              return (
                <Link
                  key={item.id}
                  to={listingUrl(item)}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md"
                  data-testid={`city-listing-${item.id}`}
                >
                  <div className="aspect-[16/10] w-full overflow-hidden bg-slate-100">
                    {foto ? (
                      <img
                        src={resolveImageUrl(foto)}
                        alt={item.titulo}
                        loading="lazy"
                        className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-slate-400">
                        {isViatura ? <Car className="h-10 w-10" /> : <Users className="h-10 w-10" />}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <h2 className="line-clamp-1 text-base font-bold text-slate-900">{item.titulo}</h2>
                    <p className="mt-1 flex items-center gap-1 text-sm text-slate-500">
                      <MapPin className="h-4 w-4" /> {item.cidade || label}
                    </p>
                    {isViatura && preco ? (
                      <p className="mt-3 text-lg font-extrabold text-emerald-600">
                        {Number(preco).toLocaleString('pt-PT')}€ <span className="text-xs font-medium text-slate-500">/ semana</span>
                      </p>
                    ) : null}
                    <span className="mt-auto pt-3 text-sm font-semibold text-emerald-600 group-hover:underline">
                      Ver anúncio →
                    </span>
                  </div>
                </Link>
              );
            })}
            </AdInlineList>
          </div>
        )}

        {/* Explorar outros distritos */}
        <section className="mt-12" data-testid="city-other-districts">
          <h2 className="mb-4 text-lg font-bold text-slate-900">
            {isViatura ? 'Viaturas TVDE por distrito' : 'Motoristas TVDE por distrito'}
          </h2>
          <div className="flex flex-wrap gap-2">
            {DISTRICTS.map((d) => (
              <Link
                key={d.slug}
                to={`/${tipoPlural}/${d.slug}`}
                className={`rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                  d.slug === cidadeSlug
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-slate-300 bg-white text-slate-600 hover:border-emerald-400 hover:text-emerald-600'
                }`}
                data-testid={`district-link-${d.slug}`}
              >
                {d.label}
              </Link>
            ))}
          </div>
        </section>
      </main>

      <MarketplaceFooter onManageClick={() => (window.location.href = '/gerir-anuncio')} />
    </div>
  );
}
